import { useState, useEffect } from 'react';

// Google Cloud Storage bucket configuration
const GCS_BUCKET_URL = 'https://storage.googleapis.com/maxea-report-card-assets';

// Alternative CDN URL for better performance and CORS handling
const GCS_CDN_URL = 'https://storage.cloud.google.com/maxea-report-card-assets';

/**
 * Service for managing Google Cloud Storage assets
 */
export class CloudAssetService {
  private static cache = new Map<string, string>();
  
  /**
   * Get the full URL for a cloud asset
   * @param assetPath - Path to the asset in the bucket (e.g., 'coach-max-gesture-right.png')
   * @param useCDN - Whether to use CDN URL for better performance
   * @returns Full URL to the asset
   */
  static getAssetUrl(assetPath: string, useCDN: boolean = false): string {
    // Remove leading slash if present
    const cleanPath = assetPath.startsWith('/') ? assetPath.slice(1) : assetPath;
    const baseUrl = useCDN ? GCS_CDN_URL : GCS_BUCKET_URL;
    return `${baseUrl}/${cleanPath}`;
  }

  /**
   * Preload an asset and cache the result
   * @param assetPath - Path to the asset
   * @returns Promise that resolves when asset is loaded
   */
  static async preloadAsset(assetPath: string): Promise<string> {
    const url = this.getAssetUrl(assetPath);
    
    // Return cached URL if available
    if (this.cache.has(url)) {
      return this.cache.get(url)!;
    }

    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        this.cache.set(url, url);
        resolve(url);
      };
      
      img.onerror = () => {
        reject(new Error(`Failed to load asset: ${assetPath}`));
      };
      
      img.src = url;
    });
  }

  /**
   * Check if asset is cached
   * @param assetPath - Path to the asset
   * @returns True if asset is cached
   */
  static isAssetCached(assetPath: string): boolean {
    const url = this.getAssetUrl(assetPath);
    return this.cache.has(url);
  }
}

/**
 * Hook for loading cloud assets with loading state
 * @param assetPath - Path to the asset in GCS bucket
 * @returns Object with url, loading state, and error
 */
export const useCloudAsset = (assetPath: string | null) => {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!assetPath) {
      setUrl(null);
      setLoading(false);
      setError(null);
      return;
    }

    // Check if already cached
    if (CloudAssetService.isAssetCached(assetPath)) {
      setUrl(CloudAssetService.getAssetUrl(assetPath));
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    CloudAssetService.preloadAsset(assetPath)
      .then((loadedUrl) => {
        setUrl(loadedUrl);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
        // Fallback to direct URL even if preload failed
        setUrl(CloudAssetService.getAssetUrl(assetPath));
      });
  }, [assetPath]);

  return { url, loading, error };
};